import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { useHost } from 'src/providers/HostProvider';
import { getAdminState } from 'src/store/admin/selectors';
import { adminActions } from 'src/store/admin/slice';
import { removeFloor } from 'src/store/floors/services';
import { useAppDispatch } from 'src/store/useAppDispatch';

import { AdministratorFloorFields } from '../AdministratorFloorForm/AdministratorFloorFields';
import { AdministratorFloorForm } from '../AdministratorFloorForm/AdministratorFloorForm';

import classes from './FloorBottomSheet.module.scss';

const Fields: FC = () => {
  const id = useField<number | undefined>('id').input.value;
  const dispatch = useAppDispatch();

  const { submit } = useForm();

  const { floorIsOpened } = useSelector(getAdminState);

  const close = useCallback(() => {
    dispatch(adminActions.closeFloor());
  }, [dispatch]);

  const saveFloor = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');

    close();
  }, [close, submit]);

  const deleteFloor = useCallback(async () => {
    if (id) await dispatch(removeFloor(id));

    close();
  }, [close, dispatch, id]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    const result: BottomSheetActionsType = [
      {
        label: id ? 'Сохранить' : 'Создать',
        variant: 'contained',
        onClick: saveFloor,
      },
    ];
    if (id)
      result.unshift({
        label: 'Удалить',
        variant: 'outlined',
        onClick: deleteFloor,
      });

    return result;
  }, [deleteFloor, id, saveFloor]);

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(adminActions.setFloorIsOpened(value));
    },
    [dispatch],
  );

  return (
    <BottomSheet
      isOpen={floorIsOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[1]}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Stair,
        title: id ? 'Редактирование этажа' : 'Создание этажа',
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        <AdministratorFloorFields />
      </div>
    </BottomSheet>
  );
};

const FormBottomSheet: FC = () => {
  return (
    <AdministratorFloorForm>
      <Fields />
    </AdministratorFloorForm>
  );
};

export const FloorBottomSheet: FC = () => {
  const { host } = useHost();

  if (!host) return null;

  return createPortal(<FormBottomSheet />, host);
};
